import { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { FieldValues, UseFormResetField, UseFormSetValue } from 'react-hook-form';
import { GetContactInformationMyMSCResponse } from '@msc-b2c/interfaces/interfaces';
import { ApiErrorModalProps, ChildInput, SpecificErrorMessage } from '@msc-b2c/interfaces/types';
import {
  FormTypeEnum,
  GetContactInformationMyMSC,
  RootState,
  closeLoader,
  getNoPostCodeCountries,
  openLoader,
} from '@msc-b2c/shared-utils';

import { getErrorContent } from '../../utils/apiUtils';

/**
 * A custom hook that fetches customer contact information from the API.
 *
 * @param userDataUpdated A boolean flag indicating if user data has been updated and needs to be refreshed
 * @param setAreUserDataOutdated A state setter function to update whether user data is outdated
 * @param setError A state setter function to handle API errors
 * @param errors Optional specific error messages to display
 * @param goldenId Optional customer identifier used for the API request
 * @returns The customer contact information or undefined if not yet loaded
 */
export const useGetCustomerContactInformation = (
  userDataUpdated: boolean,
  setAreUserDataOutdated: React.Dispatch<React.SetStateAction<boolean>>,
  setError: React.Dispatch<React.SetStateAction<ApiErrorModalProps | null>>,
  errors?: SpecificErrorMessage,
  goldenId?: string
) => {
  const [customerInfo, setCustomerInfo] = useState<
    GetContactInformationMyMSCResponse | undefined
  >();
  const prevGoldenIdRef = useRef<string | undefined>(undefined);
  const latestGoldenIdRef = useRef(goldenId);
  latestGoldenIdRef.current = goldenId;

  useEffect(() => {
    if (goldenId === prevGoldenIdRef.current) {
      return;
    }
    prevGoldenIdRef.current = goldenId;
    setCustomerInfo(undefined);
    if (goldenId) {
      setAreUserDataOutdated(true);
    }
  }, [goldenId, setAreUserDataOutdated]);

  useEffect(() => {
    if (!userDataUpdated) return;

    if (!goldenId) {
      return;
    }

    openLoader('.loader');
    const goldenIdForRequest = goldenId;

    const getCustomerContactInformation = new GetContactInformationMyMSC(false, {
      goldenId: goldenIdForRequest,
    });

    getCustomerContactInformation
      .getData()
      .then((data) => {
        if (goldenIdForRequest !== latestGoldenIdRef.current) {
          return;
        }
        setCustomerInfo(data);
      })
      .catch((error) => {
        if (goldenIdForRequest !== latestGoldenIdRef.current) {
          return;
        }
        console.error(error);
        const { title, message } = getErrorContent(error, errors);
        setError({ message, title, iconType: errors?.errorMessageIcon ?? '' });
      })
      .finally(() => {
        if (goldenIdForRequest !== latestGoldenIdRef.current) {
          return;
        }
        setAreUserDataOutdated(false);
        closeLoader('.loader');
      });
  }, [userDataUpdated, goldenId]);

  return customerInfo;
};

/**
 * A custom hook that transforms form elements based on dynamic form data from Redux state.
 * It handles populating dropdown items and setting default values when certain conditions are met.
 *
 * @param formElements - An array of form input elements to be transformed
 * @param setValue - A React Hook Form function to set values in the form
 * @returns The transformed array of form elements with updated properties
 */
export function useTransformFormData(
  formElements: ChildInput[],
  setValue: UseFormSetValue<FieldValues>,
  resetField: UseFormResetField<FieldValues>
) {
  const addressManagement = useSelector((state: RootState) => state.addressManagement);
  const [transformedFormElements, setTransformedFormElements] = useState<ChildInput[]>([]);

  useEffect(() => {
    setTransformedFormElements(
      formElements
        .filter((input) => {
          if (
            input.name === 'address.region' &&
            !addressManagement.addressData.some((address) =>
              Object.keys(address).includes('region')
            )
          ) {
            return false;
          }
          if (
            input.name === 'address.state' &&
            !addressManagement.addressData.some((address) => Object.keys(address).includes('state'))
          ) {
            return false;
          }
          if (
            input.name === 'address.province' &&
            addressManagement.addressData.length > 0 &&
            !addressManagement.addressData.some((address) =>
              Object.keys(address).includes('province')
            )
          ) {
            return false;
          }
          if (
            input.type === FormTypeEnum.DROPDOWN &&
            input.name === 'address.town' &&
            getNoPostCodeCountries().includes(addressManagement.selectedCountry)
          ) {
            return false;
          }
          return true;
        })
        .map((input) => {
          const newInput = { ...input };
          let newData: string[] = [];
          if (input.name === 'address.region') {
            newData = Array.from(
              new Set(
                addressManagement.addressData
                  .filter((address) => !!address?.region)
                  .map((address) => address.region ?? '')
              )
            );
          }
          if (input.name === 'address.state') {
            newData = Array.from(
              new Set(
                addressManagement.addressData
                  .filter((address) => !!address?.state)
                  .map((address) => address.state ?? '')
              )
            );
          }
          if (input.name === 'address.province') {
            newData = Array.from(
              new Set(
                addressManagement.addressData
                  .filter((address) => !!address?.province)
                  .map((address) => address.province ?? '')
              )
            );
          }
          if (input.name === 'address.town') {
            newData = Array.from(
              new Set(
                addressManagement.addressData
                  .filter((address) => !!address?.town)
                  .map((address) => address.town ?? '')
              )
            );
          }

          // `[]` is truthy — only rebuild/reset when verify returned matches.
          if (newInput.isCoRPrefilledAndDisabled && newData.length > 0) {
            newInput.dropdownItems = newData.map((item) => ({
              text: item.trim(),
              value: item.trim(),
              selected: false,
            }));
            resetField(newInput.name);
            if (newData.length > 0) {
              newInput.isCoRPrefilledAndDisabled = false;
            }
            if (newData.length === 1) {
              setValue(newInput.name, newInput.dropdownItems[0].value);
            }
          }
          return newInput;
        })
    );
  }, [addressManagement.addressData]);

  return transformedFormElements;
}
